import { useState, useRef, useEffect, useMemo } from 'react';
import { brand } from '../brand.js';
import {FilterDisclosure} from './filter-disclosure.jsx';

// ── Shared label ────────────────────────────────────────────
function FilterLabel({ text }) {
  return <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5">{text}</p>;
}

// ── SegmentedTabs (epic v3 — executive scorecards) ───────────
//
// ONE single-select segmented control covering both mockups' tab rows
// (pill + inline = the category pills; rect + stretch = the full-width
// navy country tabs), generalized from the near-misses already in this
// file rather than becoming a fifth one-off.
//
// A11Y: radiogroup / radio + roving tabIndex, NOT tablist / tab.
// `role="tab"` implies an associated `tabpanel` wired with
// `aria-controls`, which does not exist for a data filter — the tabs
// re-query a dashboard, they do not reveal a panel. A wrong role is
// worse than no role: it promises a relationship assistive tech will
// then look for and fail to find. Arrow / Home / End move the
// selection, which is the radio-group contract (selection follows
// focus), so a keyboard user drives it exactly like a sighted one.

// Look v3 (2026-10-24): `accent` (the default) raises the selected option
// white on the track; `navy` and `inset` fill it with the ink, the strong
// selection of the report's primary axes and of the filter drawer.
const TAB_TONE_FILL = {
  accent: null,
  // The chrome navy through its tenant variable (default `brand.ink`), so
  // a tenant's sidebar colour also fills the strong selection.
  navy: 'rgb(var(--bi-navy))',
  inset: 'rgb(var(--bi-navy))',
};

export function SegmentedTabs({
  options,
  value,
  onChange,
  shape = 'pill',
  fill = 'inline',
  tone = 'accent',
  size = 'md',
  ariaLabel,
  className = '',
}) {
  const list = Array.isArray(options) ? options : [];
  const refs = useRef([]);
  if (list.length === 0) return null;

  const enabledIdx = list.reduce((acc, o, i) => (o && !o.disabled ? [...acc, i] : acc), []);
  const selectedIdx = list.findIndex((o) => o && o.value === value);
  // Roving tabIndex: exactly ONE stop in the group, so Tab enters and
  // leaves the control instead of walking every option.
  const stopIdx = selectedIdx >= 0 ? selectedIdx : (enabledIdx[0] ?? -1);

  const move = (from, delta) => {
    if (enabledIdx.length === 0) return;
    const pos = enabledIdx.indexOf(from);
    const nextPos = pos === -1
      ? 0
      : (pos + delta + enabledIdx.length) % enabledIdx.length;
    const next = enabledIdx[nextPos];
    const el = refs.current[next];
    if (el) el.focus();
    const opt = list[next];
    if (opt && typeof onChange === 'function') onChange(opt.value);
  };

  const onKeyDown = (e, i) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); move(i, 1); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); move(i, -1); }
    else if (e.key === 'Home') { e.preventDefault(); move(enabledIdx[enabledIdx.length - 1] ?? i, 1); }
    else if (e.key === 'End') { e.preventDefault(); move(enabledIdx[0] ?? i, -1); }
  };

  // One control since the look v3: a quiet track (radius 10) with 7 px
  // options. `shape` stays in the API but no longer changes the radius
  // (D22, see the kit CHANGELOG and showcase).
  void shape;
  const pad = size === 'sm' ? 'min-h-[26px] px-2.5 text-2xs' : 'min-h-8 px-3 text-xs';
  const activeFill = TAB_TONE_FILL[tone] ?? null;

  return (
    <div
      role="radiogroup"
      data-segmented-control=""
      data-fill={fill}
      aria-label={ariaLabel}
      className={`${fill === 'stretch' ? 'flex w-full' : 'inline-flex max-w-full'} flex-wrap items-center gap-0.5 rounded-[10px] border border-neutral-200 bg-neutral-100 p-[3px] ${className}`}
    >
      {list.map((opt, i) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value ?? i}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={!!opt.disabled}
            tabIndex={i === stopIdx ? 0 : -1}
            onKeyDown={(e) => onKeyDown(e, i)}
            onClick={() => { if (!opt.disabled && typeof onChange === 'function') onChange(opt.value); }}
            className={`inline-flex items-center justify-center gap-1.5 rounded-[7px] font-semibold whitespace-nowrap transition-[background-color,color,box-shadow] duration-150 outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-40 ${pad} ${
              fill === 'stretch' ? 'min-w-0 flex-1' : ''
            } ${
              // No border on any state, so selecting never moves a
              // neighbour (the ring-not-border rule).
              selected
                ? activeFill
                  ? 'text-white shadow-sm'
                  : 'bg-white text-neutral-800 shadow-[0_1px_4px_rgb(26_18_64_/_0.125)]'
                : 'text-neutral-600 hover:bg-white/70 hover:text-neutral-800'
            }`}
            style={selected && activeFill ? { background: activeFill } : undefined}
          >
            <span className="truncate">{opt.label}</span>
            {typeof opt.count === 'number' && (
              <span className={`text-2xs tabular-nums ${selected && activeFill ? 'text-white/80' : 'text-neutral-500'}`}>
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Reusable input class tokens (h-8 baseline = 32px)
const INPUT_BASE = 'h-8 rounded-lg border bg-white text-sm text-neutral-700 outline-none transition-all focus-visible:ring-2 focus-visible:ring-accent/30';
const INPUT_IDLE = 'border-neutral-200 hover:border-neutral-300';
const INPUT_ACTIVE = 'border-accent ring-2 ring-accent/20';

// ── Option-list standard (mirror of dashboard-render/option-list.tsx) ──
//
// BI rule: option lists NEVER cap or hide data. The dropdown's scrollbar
// always represents the COMPLETE option universe; performance comes from
// VIRTUALIZATION (DOM holds only the visible window + overscan), never
// from slicing. In-dropdown search is a convenience/shortcut — scrolling
// alone must reach every option.
//
// Cardinality tiers:
//   | options     | behavior                                            |
//   |-------------|-----------------------------------------------------|
//   | <= 200      | plain dropdown, full list, no search box            |
//   | 201 - 1,000 | search box, filter applies from the 1st keystroke   |
//   | > 1,000     | autocomplete pattern: filter waits for >= 3 typed   |
//   |             | chars AND a ~300 ms pause; below the gate a subtle  |
//   |             | hint shows and the FULL universe stays scrollable   |
//
// The constants are REPLICATED here (with the same values) because
// bi-components sits BELOW dashboard-render in the dependency graph and
// cannot import from it. Source of truth for the standard + docs:
// packages/dashboard-render/src/option-list.tsx. Keep both in sync.
const SEARCH_VISIBLE_THRESHOLD = 200;
const LARGE_LIST_THRESHOLD = 1000;
const AUTOCOMPLETE_MIN_CHARS = 3;
const AUTOCOMPLETE_DEBOUNCE_MS = 300;
// Virtualization geometry: fixed row height (matches a px-3 py-1.5
// text-xs row), viewport (~8.5 rows) and overscan rows painted beyond it.
const OPTION_ROW_PX = 36;
const OPTION_VIEWPORT_PX = 240;
const OPTION_OVERSCAN = 8;
// Hint copy for the large-list gate. bi-components has no i18n plumbing
// (all its strings are inline ES) — the product pickers in
// dashboard-render use the `filterbar.typeToSearch` key instead.
const MIN_CHARS_HINT = `Escribe ${AUTOCOMPLETE_MIN_CHARS}+ letras para buscar`;

// Search/filter state shared by the option pickers below. Mirrors
// dashboard-render's useOptionSearch: query resets on every open, the
// lowercase copy is precomputed per `options` change, and above
// LARGE_LIST_THRESHOLD the filter waits for the min-chars gate + a
// debounce (only the FILTER is deferred — typing stays live; the timer
// cancels cleanly on every keystroke / close / unmount).
function useOptionSearch(options, open) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const largeList = options.length > LARGE_LIST_THRESHOLD;

  useEffect(() => {
    if (open) { setQuery(''); setDebounced(''); }
  }, [open]);

  useEffect(() => {
    if (!largeList) return undefined;
    const q = query.trim();
    if (q.length < AUTOCOMPLETE_MIN_CHARS) {
      // Below the gate the filter drops synchronously — deleting back
      // under the minimum restores the full universe immediately.
      setDebounced('');
      return undefined;
    }
    const timer = setTimeout(() => setDebounced(q), AUTOCOMPLETE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, largeList]);

  const effectiveQuery = largeList ? debounced : query.trim();
  const lower = useMemo(() => options.map(o => o.toLowerCase()), [options]);
  const filtered = useMemo(() => {
    const q = effectiveQuery.toLowerCase();
    if (!q) return options;
    const out = [];
    for (let i = 0; i < options.length; i += 1) {
      if (lower[i].includes(q)) out.push(options[i]);
    }
    return out;
  }, [effectiveQuery, options, lower]);

  return {
    query,
    setQuery,
    filtered,
    effectiveQuery,
    largeList,
    hintMinChars: largeList && query.trim().length < AUTOCOMPLETE_MIN_CHARS,
  };
}

// Hand-rolled fixed-row-height virtualized list (mirror of
// dashboard-render/option-list.tsx::VirtualOptionList — see there for
// the full rationale). Spacer paddings keep the scrollbar geometry equal
// to the COMPLETE `count` while the DOM holds only the window +
// overscan. `followIndex` (keyboard highlight — NEVER mouse hover) is
// auto-scrolled into view; the programmatic scrollTop write is mirrored
// into state because jsdom fires no scroll event for it. Rows returned
// by `renderRow` must be exactly `rowPx` tall.
function VirtualOptionList({ count, renderRow, followIndex = -1, rowPx = OPTION_ROW_PX, viewportPx = OPTION_VIEWPORT_PX, className = '' }) {
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);

  const totalPx = count * rowPx;
  // Clamp: a narrowing filter can leave scrollTop past the new end
  // (real browsers re-clamp + fire scroll; jsdom does not).
  const top = Math.min(scrollTop, Math.max(0, totalPx - viewportPx));
  const start = Math.max(0, Math.floor(top / rowPx) - OPTION_OVERSCAN);
  const end = Math.min(count, Math.ceil((top + viewportPx) / rowPx) + OPTION_OVERSCAN);

  useEffect(() => {
    if (followIndex < 0 || !scrollRef.current) return;
    const el = scrollRef.current;
    const rowTop = followIndex * rowPx;
    const rowBottom = rowTop + rowPx;
    let next = null;
    if (rowTop < el.scrollTop) next = rowTop;
    else if (rowBottom > el.scrollTop + viewportPx) next = rowBottom - viewportPx;
    if (next !== null) { el.scrollTop = next; setScrollTop(next); }
  }, [followIndex, rowPx, viewportPx]);

  const rows = [];
  for (let i = start; i < end; i += 1) rows.push(renderRow(i));

  return (
    <div
      ref={scrollRef}
      data-virtualized-options=""
      className={`overflow-y-auto overscroll-contain ${className}`}
      style={{ maxHeight: viewportPx }}
      onScroll={e => setScrollTop(e.currentTarget.scrollTop)}>
      <div style={{ paddingTop: start * rowPx, paddingBottom: (count - end) * rowPx }}>{rows}</div>
    </div>
  );
}

// ── Chip Filter ─────────────────────────────────────────────
export function ChipFilter({ options, selected, onToggle, label }) {
  return (
    <div>
      {label && <FilterLabel text={label} />}
      <div className="flex flex-wrap gap-1.5">
        {options.map(opt => {
          const active = selected.includes(opt);
          return (
            <button key={opt} onClick={() => onToggle(opt)}
              className={`h-6 rounded-full px-2.5 text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ${
                active
                  ? 'bg-accent text-white hover:bg-accent-dark'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 hover:text-neutral-800'
              }`}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Range Slider ────────────────────────────────────────────
// Dual-handle numeric range. Same interaction model as the product's
// date slider (dashboard-render/date-slider-core.tsx, PR #25/#34
// lineage), adapted to a numeric domain:
//
//  * Uncontrolled-while-dragging: the two native range inputs render
//    from LOCAL state during a drag; the host's `value` prop is only
//    synced back when no drag is in flight. A fully-controlled pair
//    whose clamp disagrees with the native drag position re-emits
//    input events per mouse move (~100/s) — the 2026-07-07 freeze
//    family of bugs.
//  * No adversarial clamp: dragging one handle past the other is
//    allowed in the raw local state; the rendered/emitted pair is the
//    sorted window, so `onChange` always sees `lo <= hi`. (The old
//    `Math.min(v, hi - 1)` clamp fought the drag and forbade lo == hi.)
//  * Nearest-handle pointer routing: the two inputs overlap full-width
//    and only the top z-index one receives pointer events — pre-fix
//    the HI input always sat on top so the LO thumb was un-grabbable.
//    The track follows the pointer's X and raises the input whose
//    thumb is nearest; the state flips at most a couple of times per
//    gesture (ref-guarded — never a render per pixel).
export function RangeSlider({ min = 0, max = 100, step = 1, value, onChange, label, format = v => v }) {
  const [propLo, propHi] = value || [min, max];
  const [local, setLocal] = useState([propLo, propHi]);
  const dragging = useRef(false);
  useEffect(() => {
    if (!dragging.current) setLocal([propLo, propHi]);
  }, [propLo, propHi]);

  const [rawLo, rawHi] = local;
  const dispLo = Math.min(rawLo, rawHi);
  const dispHi = Math.max(rawLo, rawHi);
  const span = max - min || 1;
  const pctLo = ((dispLo - min) / span) * 100;
  const pctHi = ((dispHi - min) / span) * 100;

  const move = (which, next) => {
    dragging.current = true;
    setLocal(which === 0 ? [next, rawHi] : [rawLo, next]);
    const a = which === 0 ? next : rawLo;
    const b = which === 1 ? next : rawHi;
    onChange([Math.min(a, b), Math.max(a, b)]);
  };
  const commit = () => {
    dragging.current = false;
    // Un-cross the raw pair on release so the inputs match the sorted
    // window the host saw, before the next prop sync.
    setLocal([dispLo, dispHi]);
  };

  // ── Adjacent (never overlapping) thumbs ────────────────────
  // Same pattern as dashboard-render/date-slider-core.tsx: when the two
  // 16px balls collide in pixels (lo == hi, or values close enough that
  // they overlap), render them ADJACENT — LO nudged left, HI nudged
  // right, edges touching — instead of stacked. VISUAL offset only
  // (≤ half a thumb each side): the value positions, the filled track
  // segment and the nearest-handle routing math all keep using the
  // un-shifted percents. Track width is observed via ResizeObserver
  // (fires on layout changes only — never per drag frame, so the
  // freeze-fix "no render per pixel" constraint holds). jsdom / no-RO
  // environments keep width 0 → no shift (legacy render).
  const trackRef = useRef(null);
  const [trackW, setTrackW] = useState(0);
  useEffect(() => {
    const el = trackRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setTrackW((prev) => (prev === w ? prev : w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const THUMB_PX = 16; // w-4 h-4 thumb
  const gapPx = trackW > 0 ? ((pctHi - pctLo) / 100) * trackW : Number.POSITIVE_INFINITY;
  const sepPx = gapPx < THUMB_PX ? Math.min((THUMB_PX - gapPx) / 2, THUMB_PX / 2) : 0;
  // When both thumbs coincide, put the LO input on top only when parked
  // in the upper half — otherwise the lower thumb could never be grabbed
  // from the domain's right edge (and vice versa).
  const loOnTop = rawLo === rawHi && (rawLo - min) > span / 2;

  // Nearest-handle routing (see header comment). `nearest` has only two
  // meaningful values and updates through a ref guard, so state changes
  // exactly when the nearest handle FLIPS — never per pointer move.
  const [nearest, setNearest] = useState(null);
  const nearestRef = useRef(null);
  const updateNearest = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return; // jsdom / display:none — keep fallback
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    const dLo = Math.abs(pct - pctLo);
    const dHi = Math.abs(pct - pctHi);
    let next;
    if (dLo < dHi) next = 0;
    else if (dHi < dLo) next = 1;
    // Tie (coincident thumbs / equidistant pointer): left of the shared
    // position grabs LO, right grabs HI; dead-center falls back to the
    // handle with more room to widen the window.
    else if (pct < pctLo) next = 0;
    else if (pct > pctHi) next = 1;
    else next = (pctLo + pctHi) / 2 > 50 ? 0 : 1;
    if (nearestRef.current !== next) { nearestRef.current = next; setNearest(next); }
  };
  const clearNearest = () => {
    if (nearestRef.current !== null) { nearestRef.current = null; setNearest(null); }
  };
  // Hover-tracked nearest handle wins; without hover (initial render /
  // touch first contact) fall back to the collapsed-thumbs heuristic.
  const topInput = nearest !== null ? nearest : (loOnTop ? 0 : 1);

  const inputClass = 'absolute w-full top-0 h-6 opacity-0 cursor-pointer';
  const thumbClass = 'absolute top-0.5 w-4 h-4 rounded-full bg-white border-2 border-accent shadow-sm pointer-events-none transition-transform motion-safe:hover:scale-110';

  return (
    <div>
      {label && <FilterLabel text={label} />}
      <div ref={trackRef} className="relative h-6" onPointerMove={updateNearest} onPointerDownCapture={updateNearest} onPointerLeave={clearNearest}>
        <div className="absolute top-2 left-0 right-0 h-1.5 rounded-full bg-neutral-100" />
        <div className="absolute top-2 h-1.5 rounded-full bg-accent" style={{ left: `${pctLo}%`, width: `${pctHi - pctLo}%` }} />
        <input type="range" min={min} max={max} step={step} value={rawLo}
          onChange={e => move(0, +e.target.value)}
          onPointerUp={commit} onKeyUp={commit} onBlur={commit}
          className={inputClass} style={{ zIndex: topInput === 0 ? 4 : 3 }} aria-label={`${label || 'Rango'} mínimo`} />
        <input type="range" min={min} max={max} step={step} value={rawHi}
          onChange={e => move(1, +e.target.value)}
          onPointerUp={commit} onKeyUp={commit} onBlur={commit}
          className={inputClass} style={{ zIndex: topInput === 1 ? 4 : 3 }} aria-label={`${label || 'Rango'} máximo`} />
        <div data-thumb="lo" className={thumbClass} style={{ left: `calc(${pctLo}% - ${8 + sepPx}px)` }} />
        <div data-thumb="hi" className={thumbClass} style={{ left: `calc(${pctHi}% - ${8 - sepPx}px)` }} />
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="text-xs font-semibold text-accent-dark">{format(dispLo)}</span>
        <span className="text-xs font-semibold text-accent-dark">{format(dispHi)}</span>
      </div>
    </div>
  );
}

// ── Search Filter ────────────────────────────────────────────
export function SearchFilter({ placeholder = 'Buscar...', value, onChange, label }) {
  return (
    <div>
      {label && <FilterLabel text={label} />}
      <div className="relative">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={brand.semantic.muted} strokeWidth="2" strokeLinecap="round"
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true">
          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.3-4.3"/>
        </svg>
        <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className={`${INPUT_BASE} ${INPUT_IDLE} w-full pl-9 pr-8 placeholder-neutral-400 hover:shadow-sm focus:${INPUT_ACTIVE}`} />
        {value && (
          <button onClick={() => onChange('')} aria-label="Limpiar búsqueda"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 transition-colors text-base leading-none">×</button>
        )}
      </div>
    </div>
  );
}

// ── Date Preset Filter ───────────────────────────────────────
export function DatePresetFilter({ options, selected, onSelect, label }) {
  return (
    <div>
      {label && <FilterLabel text={label} />}
      {/* Look v3: date presets raise the chosen period white on the track. */}
      <SegmentedTabs options={options} value={selected} onChange={onSelect}
        shape="rect" size="sm" ariaLabel={label} />
    </div>
  );
}

// ── Autocomplete Filter (single-select with search) ──────────
// Option list is VIRTUALIZED (never sliced): the dropdown's scrollbar
// spans the complete universe; typing is a shortcut. Above
// LARGE_LIST_THRESHOLD the filter waits for the min-chars gate +
// debounce (see the option-list standard at the top of this file).
export function AutocompleteFilter({ options, value, onChange, label, placeholder = 'Buscar o seleccionar...' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { query, setQuery, filtered, hintMinChars } = useOptionSearch(options, open);

  const handleFocus = () => setOpen(true); // useOptionSearch resets the query on open
  const handleSelect = (opt) => { onChange(opt); setOpen(false); setQuery(''); };
  const handleClear = (e) => { e.stopPropagation(); onChange(null); setQuery(''); inputRef.current?.focus(); };

  // Row height for this picker's taller rows (px-3 py-2 text-sm) and the
  // dropdown viewport (was max-h-52).
  const AC_ROW_PX = 36;
  const AC_VIEWPORT_PX = 208;

  return (
    <div ref={ref} className="relative">
      {label && <FilterLabel text={label} />}
      <div className="relative">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={open || value ? brand.accent.DEFAULT : brand.semantic.muted} strokeWidth="2" strokeLinecap="round"
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors" aria-hidden="true">
          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.3-4.3"/>
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={open ? query : (value || '')}
          placeholder={value ? value : placeholder}
          onFocus={handleFocus}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter' && filtered.length > 0) handleSelect(filtered[0]);
          }}
          className={`${INPUT_BASE} w-full pl-9 pr-8 ${
            open ? INPUT_ACTIVE : value ? 'border-accent/40 hover:border-accent/70 text-neutral-800' : INPUT_IDLE
          }`}
        />
        {value && !open ? (
          <button onClick={handleClear} aria-label="Limpiar"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded-full bg-neutral-200 text-neutral-500 hover:bg-neutral-300 hover:text-neutral-700 transition-all text-xs leading-none">×</button>
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={brand.semantic.muted} strokeWidth="2"
            className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        )}
      </div>

      {open && (
        <div data-dropdown-surface="list" className="absolute top-full left-0 mt-1 z-50 w-full rounded-lg border border-neutral-200 bg-white shadow-xl overflow-hidden">
          {hintMinChars && (
            <div className="border-b border-neutral-100 px-3 py-1.5 text-2xs text-neutral-600">
              {MIN_CHARS_HINT}
            </div>
          )}
          {filtered.length > 0 ? (
            <VirtualOptionList
              count={filtered.length}
              rowPx={AC_ROW_PX}
              viewportPx={AC_VIEWPORT_PX}
              renderRow={i => {
                const opt = filtered[i];
                return (
                  <div key={opt} style={{ height: AC_ROW_PX }}>
                    <button onClick={() => handleSelect(opt)}
                      className={`flex items-center gap-2 w-full h-full px-3 text-sm transition-colors text-left ${
                        value === opt
                          ? 'bg-accent/10 text-accent-dark font-semibold'
                          : 'text-neutral-700 hover:bg-neutral-50'
                      }`}>
                      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors ${
                        value === opt ? 'bg-accent border-accent' : 'border-neutral-300'
                      }`}>
                        {value === opt && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                      </span>
                      <span className="truncate">{opt}</span>
                    </button>
                  </div>
                );
              }}
            />
          ) : (
            <div className="px-3 py-4 text-center">
              <p className="text-xs text-neutral-600">Sin resultados para <span className="font-semibold">"{query}"</span></p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Active Filters Bar (summary pills) ───────────────────────
// Renders a compact list of active filter pills with × to remove each one.
// `items`: [{ key, label, value, onRemove }]
export function ActiveFiltersBar({ items = [], onClearAll, className = '' }) {
  if (!items.length) return null;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      <span className="text-2xs font-bold uppercase tracking-widest text-neutral-600 mr-1">Filtros activos</span>
      {items.map(it => (
        <span key={it.key} className="inline-flex items-center gap-1 rounded-md bg-accent/10 border border-accent/20 text-accent-dark px-2 h-6 text-xs font-semibold">
          <span className="text-neutral-500 font-medium">{it.label}:</span>
          <span className="truncate max-w-[140px]">{String(it.value)}</span>
          {it.onRemove && (
            <button onClick={it.onRemove} aria-label={`Quitar filtro ${it.label}`}
              className="ml-0.5 h-4 w-4 flex items-center justify-center rounded-full hover:bg-accent/20 transition-colors text-sm leading-none">×</button>
          )}
        </span>
      ))}
      {onClearAll && (
        <button onClick={onClearAll}
          className="ml-1 h-6 inline-flex items-center rounded-md px-2 text-[11px] font-semibold text-neutral-500 hover:text-negative hover:bg-red-50 transition-colors">
          Limpiar todo
        </button>
      )}
    </div>
  );
}

// ── Embedded select (dark custom dropdown) ───────────────────
// Replaces the native <select> inside EmbeddedFilterBar: the OS-rendered
// bright option list clashed with the dark banner. Styled after the
// product's SelectPicker popover (dashboard-render/GenericFilterBar.tsx
// embedded mode): dark panel, radius 10, white/10 hover, check on the
// active option. Keyboard: Enter/Space open, ArrowUp/Down move the
// highlight, Enter selects, Escape closes. Internal — not exported.
//
// The option list is VIRTUALIZED at every cardinality (see the
// option-list standard at the top of this file): the scrollbar spans
// the complete universe, nothing is ever sliced. Long lists
// (> SEARCH_VISIBLE_THRESHOLD) reveal a search input; very long lists
// (> LARGE_LIST_THRESHOLD) gate the filter behind min-chars + debounce
// with a hint row — search is a shortcut, never the only path. In
// searchable mode the highlight starts at -1 so Enter right after
// opening never commits an option the user did not pick. Row hover
// feedback is pure CSS (mouse-enter no longer moves the keyboard
// highlight — with a windowed list, hover-driven highlight would fight
// the follow-scroll and re-render per row swept).
function EmbeddedSelect({ value, options, onChange, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const ref = useRef(null);
  const inputRef = useRef(null);

  const searchable = options.length > SEARCH_VISIBLE_THRESHOLD;
  const { query, setQuery, filtered, effectiveQuery, hintMinChars } = useOptionSearch(options, open);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // An APPLIED filter highlights its first match (query + Enter commits
  // it); clearing it clears the highlight. Keyed off effectiveQuery so
  // in the debounced tier the highlight only lands once the user can
  // SEE the filtered list.
  useEffect(() => {
    if (searchable) setHighlight(effectiveQuery ? 0 : -1);
  }, [effectiveQuery, searchable]);

  const openList = () => {
    if (searchable) {
      setHighlight(-1); // no highlight until the user navigates/types
      setOpen(true);
      queueMicrotask(() => inputRef.current && inputRef.current.focus());
      return;
    }
    const idx = options.indexOf(value);
    setHighlight(idx >= 0 ? idx : 0);
    setOpen(true);
  };
  const pick = (opt) => { onChange(opt); setOpen(false); };

  const handleKey = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); openList(); }
      return; // Enter/Space toggle via the button's native click
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(filtered.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(searchable ? -1 : 0, h - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight >= 0 && filtered[highlight] != null) pick(filtered[highlight]);
      else if (searchable) setOpen(false); // Enter with no highlight only closes
    }
    // stopPropagation: Escape closes ONLY this dropdown, never a host modal.
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); }
  };

  return (
    <div ref={ref} className="relative min-w-0">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={handleKey}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={`flex h-7 w-full items-center justify-between gap-1 rounded-md border px-2 text-xs text-white outline-none transition-colors cursor-pointer bg-white/10 hover:bg-white/15 focus-visible:ring-2 focus-visible:ring-white/40 ${
          open ? 'border-white/50' : 'border-white/20 focus:border-white/50'
        }`}>
        <span className="truncate">{value}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          className={`shrink-0 text-white/60 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true">
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>
      {open && (
        <div
          data-dropdown-surface="list" role="listbox"
          aria-label={ariaLabel}
          className="absolute top-full left-0 z-30 mt-1 w-full min-w-[10rem] rounded-[10px] border border-white/15 bg-navy py-1 shadow-xl">
          {searchable && (
            <div className="flex items-center gap-1.5 border-b border-white/10 px-2 py-1.5">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                className="shrink-0 text-white/40" aria-hidden="true">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.3-4.3"/>
              </svg>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Buscar…"
                aria-label="Buscar opciones"
                className="flex-1 bg-transparent text-xs text-white placeholder-white/75 outline-none" />
            </div>
          )}
          {searchable && hintMinChars && (
            <div className="border-b border-white/10 px-3 py-1.5 text-2xs text-white/75">
              {MIN_CHARS_HINT}
            </div>
          )}
          <VirtualOptionList
            count={filtered.length}
            followIndex={highlight}
            renderRow={(i) => {
              const opt = filtered[i];
              const active = opt === value;
              return (
                <div key={opt} style={{ height: OPTION_ROW_PX }}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(opt)}
                    className={`flex w-full h-full items-center justify-between gap-2 px-3 text-left text-xs transition-colors ${
                      i === highlight
                        ? 'bg-white/15 text-white'
                        : active
                          ? 'bg-white/10 text-white'
                          : 'text-white/85 hover:bg-white/10'
                    }`}>
                    <span className="truncate">{opt}</span>
                    {active && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="shrink-0" aria-hidden="true">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    )}
                  </button>
                </div>
              );
            }}
          />
          {searchable && filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-white/75">Sin coincidencias.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Embedded Filter Bar (dark header bar, collapsible) ───────
// Text on the navy band is white at 80-85 %: the neutral-500/600 it used
// measured 2.65:1 down to 1.49:1 (QA look v3 F21, 2026-10-26).
// `filters` items control the internal select values. When `onChange` is
// provided, the bar calls onChange(filterKey, newValue). `onClear` wires
// the top-right "Clear" button.
export function EmbeddedFilterBar({ filters, children, defaultOpen = true, title = 'FILTERS', values, onChange, onClear }) {
  const [open, setOpen] = useState(defaultOpen);
  const activeCount = filters.filter(f => {
    const v = values ? values[f.key] : f.default;
    return v && v !== (f.default || 'All');
  }).length;
  return (
    <FilterDisclosure expanded={open} onExpandedChange={setOpen} surface="embedded"
      className="rounded-xl bg-navy text-white"
      summary={id=><div className="flex items-center gap-3 px-4 py-3">
        <button type="button" onClick={()=>setOpen(v=>!v)} aria-controls={id} aria-expanded={open}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold">
          <span aria-hidden="true">{open?'⌃':'⌄'}</span>{title}
          {activeCount>0&&<span className="rounded-full px-1.5">{activeCount}</span>}
        </button>
        <span className="text-xs text-white/80 flex-1">{filters.map(f=>values?.[f.key]||f.default||'All').join(' · ')}</span>
        {onClear&&<button type="button" onClick={onClear} className="text-xs text-white/85 underline">Clear</button>}
      </div>}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 px-4 pb-4">
        {filters.map(f=><div key={f.key} className="flex flex-col gap-1 min-w-0">
          <label className="text-2xs font-semibold uppercase tracking-wide text-white/80">{f.label}{f.required&&' *'}</label>
          <EmbeddedSelect value={values?.[f.key]??f.default??'All'} options={[f.default||'All',...(f.options||[]).filter(o=>o!==(f.default||'All'))]}
            onChange={v=>onChange?.(f.key,v)} ariaLabel={f.label}/>
        </div>)}
      </div>{children}
    </FilterDisclosure>
  );
}
